import { z } from 'zod'
import { streamSentimentSchema } from './stream'

/**
 * Editing the Library's articles (CLI-1021): one article or a selection, one value per field. An
 * edit is stored as a hit override (`public.subscription_hit_overrides`, under the sub-account) and
 * the analytics backend applies it when it next builds the workspace's cache — so the editor's
 * browser shows it at once (lib/stream-edits.ts in the SPA) and everyone else after that build.
 *
 * The fields are the stream row's own names. Topics and tags are changed differently: an id is
 * added to an article or taken off it (`topics` / `tags` below), kept in Headline's own tables and
 * laid over the build's stamp whenever the stream is read, like a removal — so everyone sees it at
 * once, and only in the stream. They never go through an override: the Library's tags are
 * Headline's tag rows (`TagIds`), not the payload's `Tags` strings an override would set.
 */
export const STREAM_EDIT_FIELDS = [
  'headline',
  'outlet',
  'author',
  'sentiment',
  'classification',
  'classificationL2',
  'reach',
] as const
export const streamEditFieldSchema = z.enum(STREAM_EDIT_FIELDS)
export type StreamEditField = z.infer<typeof streamEditFieldSchema>

/** Hits per edit call. The Library sends a larger selection in batches of this size. */
export const STREAM_EDIT_BATCH = 500

const text = z.string().trim().min(1).max(500)

/** The values an edit sets, by field — any subset. */
export const streamEditValuesSchema = z.strictObject({
  headline: text.optional(),
  outlet: text.optional(),
  author: text.optional(),
  sentiment: streamSentimentSchema.optional(),
  classification: text.optional(),
  classificationL2: text.optional(),
  /** An audience; the build applies it only while the stream shows reach. */
  reach: z.number().int().positive().max(1e12).optional(),
})
export type StreamEditValues = z.infer<typeof streamEditValuesSchema>

/** The topics and tags an article is in, changed: ids added to it and ids taken off it. */
export const STREAM_MAPPING_FIELDS = ['topics', 'tags'] as const
export const streamMappingFieldSchema = z.enum(STREAM_MAPPING_FIELDS)
export type StreamMappingField = z.infer<typeof streamMappingFieldSchema>

/** Ids one change may name per kind — a person picking chips, not a script. */
export const STREAM_MAPPING_MAX_IDS = 50

const mappingIds = z.array(z.uuid()).max(STREAM_MAPPING_MAX_IDS).default([])

export const streamMappingChangeSchema = z
  .strictObject({ add: mappingIds, remove: mappingIds })
  .refine((change) => !change.add.some((id) => change.remove.includes(id)), {
    message: 'An id is either added or removed, not both.',
  })
export type StreamMappingChange = z.infer<typeof streamMappingChangeSchema>

const changes = (change: StreamMappingChange | undefined) =>
  change ? change.add.length + change.remove.length : 0

export const editStreamHitsSchema = z
  .object({
    /** Warehouse hit ids, as the stream rows carry them. */
    hitIds: z.array(z.uuid()).min(1).max(STREAM_EDIT_BATCH),
    set: streamEditValuesSchema.default({}),
    /** Topics put on or taken off these articles — the workspace's own topics only. */
    topics: streamMappingChangeSchema.optional(),
    /** Tags put on or taken off these articles — the workspace's own tags only. */
    tags: streamMappingChangeSchema.optional(),
    /** Fields put back to what the build has: their edits (or topic / tag changes) are taken out. */
    clear: z
      .array(z.enum([...STREAM_EDIT_FIELDS, ...STREAM_MAPPING_FIELDS]))
      .max(STREAM_EDIT_FIELDS.length + STREAM_MAPPING_FIELDS.length)
      .default([]),
  })
  .refine(
    (edit) =>
      Object.keys(edit.set).length > 0 ||
      edit.clear.length > 0 ||
      changes(edit.topics) + changes(edit.tags) > 0,
    { message: 'An edit sets or resets at least one field.' },
  )
  .refine(
    (edit) =>
      !edit.clear.some((field) =>
        field === 'topics' || field === 'tags'
          ? changes(edit[field]) > 0
          : edit.set[field] !== undefined,
      ),
    { message: 'A field is either set or reset, not both.' },
  )
export type EditStreamHits = z.input<typeof editStreamHitsSchema>

export const editStreamHitsResponseSchema = z.object({
  /** Rows written — a reset of an article nobody edited writes none. */
  edited: z.number().int(),
  /** When the edit was stored (the server's clock): a cache built after this carries it. */
  editedAt: z.iso.datetime(),
})
export type EditStreamHitsResponse = z.infer<typeof editStreamHitsResponseSchema>

const storedChange = z.object({ add: z.array(z.string()), remove: z.array(z.string()) })

/** One article's stored edits, by field — what the edit dialog marks as edited. */
export const streamHitEditsSchema = z.object({
  values: streamEditValuesSchema,
  /** Its topic and tag changes, by id. */
  mappings: z.object({ topics: storedChange, tags: storedChange }),
})
export type StreamHitEdits = z.infer<typeof streamHitEditsSchema>

/**
 * What the edit dialog offers for topics and tags: the stream's topics (its own and its rollup
 * members', in attachment order) and the tags of the workspace and its rollup members.
 */
export const streamEditOptionsSchema = z.object({
  topics: z.array(z.object({ id: z.string(), name: z.string() })),
  tags: z.array(z.object({ id: z.string(), value: z.string(), entity: z.string() })),
})
export type StreamEditOptions = z.infer<typeof streamEditOptionsSchema>
